import axios from 'axios'
import { Message, MessageBox } from 'element-ui'
import router from '@/router'
import { getToken, getSignSecret, clearLoginInfo } from '@/utils/auth'
import { buildSignHeaders } from '@/utils/sign'

const request = axios.create({
  baseURL: '/yq-admin',
  timeout: 15000
})

// 登录过期类错误码（与后端 BusinessException 对齐）
const AUTH_ERROR_CODES = new Set([10005, 10006, 10007, 10008])
let handlingAuthExpiry = false

function handleAuthExpiry() {
  if (handlingAuthExpiry) {
    return
  }
  handlingAuthExpiry = true

  MessageBox.alert('登录状态已过期，请重新登录。', '登录失效', {
    confirmButtonText: '确定',
    closeOnClickModal: false,
    closeOnPressEscape: false,
    showClose: false,
    type: 'warning'
  }).finally(() => {
    clearLoginInfo()
    const current = router.currentRoute
    if (current.path !== '/login') {
      router.replace({ path: '/login', query: { redirect: current.fullPath } })
    }
    handlingAuthExpiry = false
  })
}

// 登录失效已由全局确认框接管，保持请求挂起可避免页面继续抛出未处理异常。
function waitForRelogin() {
  return new Promise(() => {})
}

request.interceptors.request.use(async config => {
  // 登录等公开接口不带鉴权
  if (config.skipAuth) {
    return config
  }

  const token = getToken()
  const signSecret = getSignSecret()

  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }

  // 登录后业务接口需要签名头：X-Timestamp / X-Nonce / X-Sign
  if (token && signSecret) {
    try {
      const signHeaders = await buildSignHeaders(config, request.defaults.baseURL, signSecret)
      config.headers = {
        ...config.headers,
        ...signHeaders
      }
    } catch (e) {
      Message.error('请求签名生成失败')
      return Promise.reject(e)
    }
  }

  return config
})

request.interceptors.response.use(
  res => {
    if (res.config.rawResponse) {
      return res
    }
    const data = res.data
    if (data.code !== 200) {
      // token / 登录态失效由全局确认框统一处理。
      if (AUTH_ERROR_CODES.has(data.code)) {
        handleAuthExpiry()
        return waitForRelogin()
      }
      Message.error(data.message || '请求失败')
      return Promise.reject(new Error(data.message || '请求失败'))
    }
    return data
  },
  err => {
    const status = err.response && err.response.status
    const msg = (err.response && err.response.data && err.response.data.message) || err.message || '网络异常'
    if (status === 401) {
      handleAuthExpiry()
      return waitForRelogin()
    }
    Message.error(msg)
    return Promise.reject(err)
  }
)

export default request
